import React, { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import { getShortlistNextSortOrder } from '@modules/shortlist/constants'
import { TableHeadCellProps } from '@modules/shortlist/types'
import { Typography } from '@mui/material'

import SortArrow from './SortArrow'
import { ColumnResizer, ColumnResizerDimmer, StyledTableCell, TableCellInnerContent, TableHeadCellInner } from './index.styles'

const DEFAULT_CELL_WIDTH = 200

const TableHeadCell = ({
  fieldName,
  restCellProps,
  cellComponent,
  columnTitle,
  isSortable,
  isSortActive,
  sortOrder,
  updateConfig,
  onSortChange,
  optionalColumnsConfig,
  customField,
}: TableHeadCellProps) => {
  const defaultCellWidth = restCellProps?.$cellWidth
  const columnWidth = ((optionalColumnsConfig && optionalColumnsConfig[customField?.id || fieldName]?.width) ||
    defaultCellWidth ||
    0) as number
  const [isHovered, setIsHovered] = useState(false)
  const nextSortOrder = getShortlistNextSortOrder(isSortActive, sortOrder, fieldName)

  const [resizeState, setResizeState] = useState({
    active: false,
    widthValue: 0,
  })

  const [resizeWidth, setResizeWidth] = useState(DEFAULT_CELL_WIDTH)

  useEffect(() => {
    if (['createdAt'].includes(fieldName) && columnWidth < 120) {
      setResizeWidth(120)
    } else if (fieldName === 'updatedAt' && columnWidth < 170) {
      setResizeWidth(170)
    } else if (fieldName === 'nda' && columnWidth < 94) {
      setResizeWidth(94)
    } else if (columnWidth || defaultCellWidth) {
      // @ts-expect-error fix typing
      setResizeWidth(columnWidth || defaultCellWidth)
    }
  }, [columnWidth, defaultCellWidth, fieldName])

  const resizeFrame = (e: React.MouseEvent) => {
    const { active, widthValue } = resizeState
    if (active) {
      const xDiff = Math.abs(widthValue - e.clientX)
      const newW = widthValue > e.clientX ? resizeWidth - xDiff : resizeWidth + xDiff

      setResizeState({ ...resizeState, widthValue: e.clientX })

      if (['lastEvent', 'note', 'futureEvents', 'phase', 'createdAt'].includes(fieldName) && newW < 120) {
        setResizeWidth(120)
      } else if (['contactStatus', 'tagId'].includes(fieldName) && newW < 200) {
        setResizeWidth(200)
      } else if (['updatedAt'].includes(fieldName) && newW < 170) {
        setResizeWidth(170)
      } else if (['priority'].includes(fieldName) && newW < 145) {
        setResizeWidth(145)
      } else if (['nda'].includes(fieldName) && newW < 94) {
        setResizeWidth(94)
      } else if (customField && newW < 150) {
        setResizeWidth(150)
      } else {
        setResizeWidth(newW)
      }
    }
  }

  const startResize = (e: React.MouseEvent) => {
    setResizeState({
      active: true,
      widthValue: e.clientX,
    })
  }

  const handleMouseUp = () => {
    setResizeState({ ...resizeState, active: false })
    if (optionalColumnsConfig && columnWidth !== resizeWidth) {
      updateConfig({
        ...optionalColumnsConfig,
        [customField?.id || fieldName]: {
          visible: true,
          ...optionalColumnsConfig[fieldName],
          width: resizeWidth,
        },
      })
    } else if (!customField && isSortable) {
      onSortChange?.({ fieldName, sortOrder: nextSortOrder })
    }
  }

  const showColumnResizer = false // fieldName !== 'stretchColumn'

  return (
    <StyledTableCell
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...restCellProps}
      $header
      key={`column-head-${fieldName}`}
      data-testid={`head-${fieldName}_testid`}
      onMouseMove={resizeFrame}
      onMouseUp={handleMouseUp}
      $cellWidth={resizeWidth}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableHeadCellInner>
        {cellComponent ? (
          <TableCellInnerContent>{cellComponent(customField)}</TableCellInnerContent>
        ) : (
          <TableCellInnerContent>
            <Typography variant="h3">{columnTitle && <Trans>{columnTitle}</Trans>}</Typography>
            {isSortable && <SortArrow isActive={isSortActive} order={isSortActive ? sortOrder : null} isHovered={isHovered} />}
          </TableCellInnerContent>
        )}
        {showColumnResizer ? <ColumnResizer onMouseDown={startResize} /> : null}
        {resizeState?.active ? <ColumnResizerDimmer /> : null}
      </TableHeadCellInner>
    </StyledTableCell>
  )
}

export default TableHeadCell
